/**
 * Unified gesture state machine for a single DraftScene card.
 *
 * Coordinates two pointer-event responsibilities on the same card:
 *   - long-press-to-commit (the safety contract per design doc
 *     Premise 8 — the player holds 600 ms on the always-detailed card
 *     face before a permadeath-weight pick commits)
 *   - tap-to-skip-reveal (any pointerdown during the offer-reveal
 *     animation consumes the gesture without arming long-press)
 *
 * The FRONT/BACK flip died 2026-08-02 (first-friend playtest: "flip
 * mechanism isn't that important... all of the info is on the back so
 * it can just be the main" — the same conclusion the 2026-05-13
 * layout session had already reached, see DRAFT_CARD_LAYOUT_HANDOFF).
 * Four states encode the lifecycle: IDLE, REVEALING, LONG_PRESSING,
 * COMMITTING. Transitions are driven by external methods invoked from
 * the DOM shell — the machine itself is pure logic (no DOM access).
 *
 * Multi-card lockout is implemented via a module-level Set of all
 * currently-LONG_PRESSING machines + the shared `isAnyCardLongPressing`
 * predicate. When ANY machine is in LONG_PRESSING, other machines'
 * tap handlers are no-op'd (Premise 8 safety contract).
 *
 * Reasoning for the pure-logic split: vitest is Node-only (no jsdom).
 * The machine + setPointerCapture stub via hooks lets us unit-test
 * state transitions + capture/release ordering without a real DOM.
 * Mirrors dialogueOverlayLogic.ts / dialogueOverlay.ts.
 */

export type DraftCardGestureState =
  | 'IDLE'
  | 'REVEALING'
  | 'LONG_PRESSING'
  | 'COMMITTING';

export interface DraftCardGestureHooks {
  /** Long-press completed; the pick pipeline should fire. The shell
   *  binds this to DraftScene.pick(slot). */
  onCommit: () => void;
  /** Long-press timer started. The shell binds this to the SVG
   *  progress ring's keyframe start (clockwise from 12 o'clock). */
  onRingStart: () => void;
  /** Long-press timer fired (600 ms reached). The shell binds this to
   *  the ring's full-completion visual + the commit beat. Fires
   *  immediately before onCommit. */
  onRingFire: () => void;
  /** Long-press cancelled before threshold. The shell binds this to
   *  the ring's reverse-to-empty animation (100 ms) + an optional
   *  audio cue. */
  onRingCancel: () => void;
  /** DOM request: capture the pointer for the long-press path. The
   *  shell binds this to `target.setPointerCapture(pointerId)`. */
  requestPointerCapture: (pointerId: number) => void;
  /** DOM request: release the captured pointer on terminal transition
   *  (COMMITTING or cancel-to-IDLE). The shell binds this to
   *  `target.releasePointerCapture(pointerId)`. */
  releasePointerCapture: (pointerId: number) => void;
}

export interface DraftCardGestureOpts {
  /** Initial state. Defaults to 'REVEALING' in DraftScene since the
   *  reveal animation (PR 2b) runs at scene mount; the constructor
   *  default is 'IDLE' for shell-less tests. */
  initialState?: DraftCardGestureState;
  hooks: DraftCardGestureHooks;
}

/**
 * Shared module-level set of currently-LONG_PRESSING machines. Drives
 * the cross-card lockout: while ANY card is in LONG_PRESSING, the
 * other cards' tap handlers no-op. The set is module-level rather
 * than passed in because the lockout is a global property of the
 * draft surface — any DraftScene mount creates its own machines and
 * tears them down together, so cross-mount leakage is impossible by
 * construction (and the set is empty between mounts).
 */
const activeLongPresses = new Set<DraftCardGestureMachine>();

/** True IFF any DraftCardGestureMachine instance is currently in the
 *  LONG_PRESSING state. The lockout predicate the DOM shell consults
 *  before forwarding taps on other cards. */
export function isAnyCardLongPressing(): boolean {
  return activeLongPresses.size > 0;
}

/** TEST ONLY: clear the module-level lockout set. vitest's `beforeEach`
 *  wipes runtime state; tests that exercise the lockout call this to
 *  start from a clean slate. Production code never calls it. */
export function _resetActiveLongPressesForTests(): void {
  activeLongPresses.clear();
}

export class DraftCardGestureMachine {
  private _state: DraftCardGestureState;
  private hooks: DraftCardGestureHooks;
  private capturedPointerId: number | null = null;

  constructor(opts: DraftCardGestureOpts) {
    this._state = opts.initialState ?? 'IDLE';
    this.hooks = opts.hooks;
  }

  get state(): DraftCardGestureState {
    return this._state;
  }

  /** Predicate for the DOM shell to check before forwarding a tap to a
   *  DIFFERENT card. Returns true IFF THIS machine is mid-long-press. */
  isLongPressing(): boolean {
    return this._state === 'LONG_PRESSING';
  }

  /** Called by the shell when the offer-reveal animation completes
   *  naturally. REVEALING → IDLE. No-op in other states. */
  revealComplete(): void {
    if (this._state === 'REVEALING') {
      this._state = 'IDLE';
    }
  }

  /**
   * Tap detected on the card (pointerdown + pointerup within tap
   * window, drift below threshold — gestureRouter.onTap handles the
   * timing/drift detection; this method receives only the semantic
   * tap event).
   *
   * State transitions:
   *   REVEALING    → IDLE   (tap-to-skip; consumes gesture)
   *   IDLE         → no-op  (there is nothing to flip — the detailed
   *                          face is the only face; selection is
   *                          hold-only so a stray tap can never
   *                          commit)
   *   LONG_PRESSING → no-op (gestureRouter's tap window is 200 ms;
   *                          a tap in LONG_PRESSING means the
   *                          long-press was already cancelled)
   *   COMMITTING   → no-op  (terminal state; ignore late taps)
   */
  tap(): void {
    if (this._state === 'REVEALING') {
      this._state = 'IDLE';
    }
    // IDLE / LONG_PRESSING / COMMITTING — taps are no-op.
  }

  /**
   * Long-press timer started (pointerdown on the card). Transitions
   * IDLE → LONG_PRESSING. Emits requestPointerCapture then onRingStart
   * for the SVG progress ring sweep. Registers in the cross-card
   * lockout set so `isAnyCardLongPressing()` returns true while this
   * gesture is in flight. No-op outside IDLE (REVEALING pointers are
   * consumed by the skip path; LONG_PRESSING / COMMITTING can't
   * re-arm).
   */
  longPressStarted(pointerId: number): void {
    if (this._state !== 'IDLE') {
      return;
    }
    this._state = 'LONG_PRESSING';
    this.capturedPointerId = pointerId;
    activeLongPresses.add(this);
    // Request pointer capture FIRST so subsequent pointer events stay
    // anchored to the originating card (Premise 11 — keeps drift +
    // pointerup detection consistent even when the pointer drifts off
    // visible card geometry). Then the ring start hook so the visual
    // begins at the moment of touch.
    this.hooks.requestPointerCapture(pointerId);
    this.hooks.onRingStart();
  }

  /**
   * Long-press timer fired (600 ms reached, no drift / cancel /
   * release before threshold). LONG_PRESSING → COMMITTING. Emits
   * onRingFire (ring at full) THEN onCommit (pick pipeline). Releases
   * pointer capture + removes from the lockout set.
   *
   * COMMITTING is a terminal state — no further transitions. The
   * scene tears down ~1.4 s after commit and the machine instance is
   * discarded.
   */
  longPressFired(): void {
    if (this._state !== 'LONG_PRESSING') return;
    this._state = 'COMMITTING';
    activeLongPresses.delete(this);
    if (this.capturedPointerId !== null) {
      this.hooks.releasePointerCapture(this.capturedPointerId);
      this.capturedPointerId = null;
    }
    this.hooks.onRingFire();
    this.hooks.onCommit();
  }

  /**
   * Long-press cancelled before the 600 ms threshold. Fires for any
   * of: pointerup before threshold, pointercancel, pointermove with
   * drift > 16 px from origin, pointerleave (in environments where
   * setPointerCapture isn't supported), or multitouch.
   * LONG_PRESSING → IDLE. Emits onRingCancel (ring reverses to
   * empty over 100 ms). Releases pointer capture + removes from the
   * lockout set.
   *
   * No-op in non-LONG_PRESSING states — idempotent against double-
   * cancels from the shell.
   */
  longPressCancelled(): void {
    if (this._state !== 'LONG_PRESSING') return;
    this._state = 'IDLE';
    activeLongPresses.delete(this);
    if (this.capturedPointerId !== null) {
      this.hooks.releasePointerCapture(this.capturedPointerId);
      this.capturedPointerId = null;
    }
    this.hooks.onRingCancel();
  }

  /**
   * Dispose-time cleanup. Removes the machine from the lockout set
   * if it's mid-press, without firing any hooks (the DOM is being
   * torn down — emitting visual cues would be noise). The shell calls
   * this in its SHUTDOWN handler.
   */
  destroy(): void {
    activeLongPresses.delete(this);
    this.capturedPointerId = null;
  }
}
